// FE-048 — Commitment Attachments panel.
//
// The Commitment detail page hosts this section directly under the
// Subtasks block. Behaviour:
//
// * Empty state with copy "Attach files, evidence, or supporting
//   documents for this Commitment.".
// * Drag-and-drop or file-picker upload — each file is hashed,
//   presigned via the Account Documents endpoint, PUT to S3, then
//   finalized via the Commitment-scoped finalize endpoint so the
//   resulting File lands in Account Documents *and* gets linked to
//   the Commitment in one round-trip.
// * "Link existing document" opens a modal that shows the same
//   Account / PlanYear file cabinet the broker browses today; picking
//   a file links it to the Commitment via the FileLink table.
// * Each row offers Download (opens the presigned URL in a new tab,
//   matching DocumentLibrary) and Unlink (confirmation dialog reminds
//   the broker the file remains in Account Documents).

import {
  Download, ExternalLink, FileIcon, Link2, Loader2,
  Paperclip, Unlink,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { presignDocumentUpload, sha256Hex } from "@/components/documents";

import { LinkExistingDocumentModal } from "./LinkExistingDocumentModal";
import {
  useCommitmentAttachmentsQuery,
  useFinalizeCommitmentAttachmentMutation,
  useUnlinkCommitmentAttachmentMutation,
  usePreviewCommitmentAttachment,
} from "./hooks";
import type { CommitmentAttachmentRow } from "./types";


export function CommitmentAttachmentsPanel({
  commitmentId,
  accountId,
  planYearId,
  readOnly = false,
}: {
  commitmentId: string;
  /** The Commitment's Account — required to upload via the Account
   * Documents presign endpoint. When ``null`` the panel renders a
   * locked empty state because there's no place to file the upload. */
  accountId: string | null;
  /** Optional Plan year — when set the linked-document picker
   * defaults to the plan-year folder. */
  planYearId: string | null;
  /** Soft-disable upload + link affordances (mirrors the rest of the
   * detail page). Existing rows still preview/download. */
  readOnly?: boolean;
}) {
  const query = useCommitmentAttachmentsQuery(commitmentId);
  const finalizeMutation = useFinalizeCommitmentAttachmentMutation(
    commitmentId,
  );
  const unlinkMutation = useUnlinkCommitmentAttachmentMutation(commitmentId);
  const preview = usePreviewCommitmentAttachment(commitmentId);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [pendingUnlinkId, setPendingUnlinkId] = useState<string | null>(null);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Show "Reminder: file remains in Account Documents" copy after
  // each successful unlink so the broker isn't worried they nuked
  // the underlying document.
  const [unlinkNotice, setUnlinkNotice] = useState<string | null>(null);
  // Row pending the branded unlink confirmation (replaces
  // ``window.confirm`` so the prompt matches the rest of the app).
  const [unlinkTarget, setUnlinkTarget] =
    useState<CommitmentAttachmentRow | null>(null);

  useEffect(() => {
    if (!finalizeMutation.isPending) return;
    setUploadStatus("Uploading…");
  }, [finalizeMutation.isPending]);

  async function handleFiles(fileList: FileList | File[] | null): Promise<void> {
    if (!fileList || !accountId) return;
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setUploadError(null);
    for (const file of files) {
      try {
        setUploadStatus(`Uploading ${file.name}…`);
        const hash = await sha256Hex(file);
        const presign = await presignDocumentUpload(
          { hostKind: "account", accountId },
          {
            filename: file.name,
            content_type: file.type || "application/octet-stream",
            size_bytes: file.size,
            hash_sha256_hex: hash,
          },
        );
        const putRes = await fetch(presign.upload_url, {
          method: "PUT",
          headers: presign.headers,
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(
            `upload failed: ${putRes.status} ${putRes.statusText}`,
          );
        }
        await finalizeMutation.mutateAsync({
          filename: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          storage_key: presign.storage_key,
          hash_sha256_hex: hash,
        });
        setUploadStatus(`Uploaded ${file.name}`);
      } catch (err) {
        setUploadStatus(null);
        setUploadError(
          err instanceof Error ? err.message : "Couldn't upload that file.",
        );
        return;
      }
    }
    setUploadStatus(null);
  }

  async function handlePreview(row: CommitmentAttachmentRow): Promise<void> {
    setUploadError(null);
    try {
      const next = await preview(row.link_id);
      window.open(next.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "Couldn't open the file.",
      );
    }
  }

  async function handleDownload(row: CommitmentAttachmentRow): Promise<void> {
    // Same preview round-trip but with ``?disposition=attachment`` so
    // the presigned URL forces a save dialog instead of inline render.
    // We navigate via ``window.location`` rather than ``window.open``
    // so the browser keeps the existing tab and lands the download in
    // the user's default download folder.
    setUploadError(null);
    try {
      const next = await preview(row.link_id, { disposition: "attachment" });
      window.location.href = next.url;
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : "Couldn't download the file.",
      );
    }
  }

  function requestUnlink(row: CommitmentAttachmentRow): void {
    setUnlinkTarget(row);
  }

  function performUnlink(row: CommitmentAttachmentRow): void {
    setUnlinkTarget(null);
    setPendingUnlinkId(row.link_id);
    unlinkMutation.mutate(row.link_id, {
      onSuccess: () => {
        setPendingUnlinkId(null);
        setUnlinkNotice(
          `Removed ${row.filename}. The file remains in Account `
          + `Documents.`,
        );
      },
      onError: (err) => {
        setPendingUnlinkId(null);
        setUploadError(
          err instanceof Error
            ? err.message
            : "Couldn't unlink that file.",
        );
      },
    });
  }

  const rows = query.data?.rows ?? [];
  const canWrite = !readOnly && accountId !== null;

  return (
    <section
      data-testid="commitment-detail-attachments"
      className="flex flex-col gap-2"
      onDragOver={(e) => {
        if (!canWrite) return;
        if (!Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={(e) => {
        if (!canWrite) return;
        if (!Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        setDragging(false);
        void handleFiles(e.dataTransfer.files);
      }}
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">
          Attachments
        </h2>
        {canWrite && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="commitment-attachments-link-existing"
              onClick={() => setLinkPickerOpen(true)}
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              <Link2 size={12} aria-hidden="true" />
              Link existing
            </button>
            <button
              type="button"
              data-testid="commitment-attachments-upload"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-md bg-teal px-2 py-1 text-xs font-medium text-text-inverse hover:opacity-90"
            >
              <Paperclip size={12} aria-hidden="true" />
              Upload file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              data-testid="commitment-attachments-file-input"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) void handleFiles(e.target.files);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
          </div>
        )}
      </header>

      {dragging && canWrite && (
        <div
          data-testid="commitment-attachments-dropzone-active"
          className="rounded-md border-2 border-dashed border-teal bg-teal-tint/40 px-3 py-4 text-xs text-teal"
        >
          Drop files to attach
        </div>
      )}

      {query.isPending && (
        <p
          data-testid="commitment-attachments-loading"
          className="text-xs text-text-tertiary"
        >
          Loading attachments…
        </p>
      )}
      {query.isError && (
        <p
          role="alert"
          data-testid="commitment-attachments-error"
          className="rounded-md bg-ember-tint px-3 py-2 text-xs text-ember-ink"
        >
          Couldn't load attachments.
        </p>
      )}
      {query.isSuccess && rows.length === 0 && !uploadStatus && (
        <p
          data-testid="commitment-attachments-empty"
          className="text-xs italic text-text-tertiary"
        >
          {accountId === null
            ? "Set an Account to attach evidence to this Commitment."
            : "Attach files, evidence, or supporting documents for this Commitment."}
        </p>
      )}
      {rows.length > 0 && (
        <ul
          role="list"
          data-testid="commitment-attachments-rows"
          className="flex flex-col gap-1"
        >
          {rows.map((row) => (
            <AttachmentRow
              key={row.link_id}
              row={row}
              onPreview={handlePreview}
              onDownload={handleDownload}
              onUnlink={requestUnlink}
              isUnlinking={
                pendingUnlinkId === row.link_id
                && unlinkMutation.isPending
              }
              readOnly={!canWrite}
            />
          ))}
        </ul>
      )}
      {uploadStatus && (
        <p
          data-testid="commitment-attachments-status"
          className="inline-flex items-center gap-1 text-xs text-text-tertiary"
        >
          <Loader2 size={11} aria-hidden="true" className="animate-spin" />
          {uploadStatus}
        </p>
      )}
      {unlinkNotice && (
        <p
          role="status"
          data-testid="commitment-attachments-unlink-notice"
          className="text-xs text-text-tertiary"
        >
          {unlinkNotice}
        </p>
      )}
      {uploadError && (
        <p
          role="alert"
          data-testid="commitment-attachments-upload-error"
          className="rounded-md bg-ember-tint px-3 py-2 text-xs text-ember-ink"
        >
          {uploadError}
        </p>
      )}

      <LinkExistingDocumentModal
        open={linkPickerOpen}
        onClose={() => setLinkPickerOpen(false)}
        commitmentId={commitmentId}
        accountId={accountId}
        planYearId={planYearId}
      />

      <ConfirmDialog
        open={unlinkTarget !== null}
        title="Unlink from this Commitment?"
        body={
          unlinkTarget
            ? `Remove ${unlinkTarget.filename} from this Commitment? `
              + `The file will remain in Account Documents.`
            : ""
        }
        confirmLabel="Unlink"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => unlinkTarget && performUnlink(unlinkTarget)}
        onCancel={() => setUnlinkTarget(null)}
        testId="commitment-attachments-unlink-confirm"
      />
    </section>
  );
}


function AttachmentRow({
  row, onPreview, onDownload, onUnlink, isUnlinking, readOnly,
}: {
  row: CommitmentAttachmentRow;
  onPreview: (row: CommitmentAttachmentRow) => void;
  onDownload: (row: CommitmentAttachmentRow) => void;
  onUnlink: (row: CommitmentAttachmentRow) => void;
  isUnlinking: boolean;
  readOnly: boolean;
}) {
  return (
    <li
      data-testid={`commitment-attachments-row-${row.link_id}`}
      className="flex items-center gap-2 rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm"
    >
      <FileIcon size={14} className="text-text-tertiary" aria-hidden="true" />
      <button
        type="button"
        data-testid={`commitment-attachments-preview-${row.link_id}`}
        onClick={() => onPreview(row)}
        className="flex-1 min-w-0 text-left text-text-primary hover:underline"
      >
        <span className="block truncate">{row.filename}</span>
        {row.document_node_path && (
          <span className="block truncate text-[11px] text-text-tertiary">
            Account Documents · {row.document_node_path}
          </span>
        )}
      </button>
      <span className="text-[11px] text-text-tertiary">
        {formatSize(row.size_bytes)}
      </span>
      <button
        type="button"
        data-testid={`commitment-attachments-open-${row.link_id}`}
        aria-label={`Open ${row.filename} in a new tab`}
        title="Open in new tab"
        onClick={() => onPreview(row)}
        className="text-text-tertiary hover:text-teal"
      >
        <ExternalLink size={12} aria-hidden="true" />
      </button>
      <button
        type="button"
        data-testid={`commitment-attachments-download-${row.link_id}`}
        aria-label={`Download ${row.filename}`}
        title="Download"
        onClick={() => onDownload(row)}
        className="text-text-tertiary hover:text-teal"
      >
        <Download size={12} aria-hidden="true" />
      </button>
      {!readOnly && (
        <button
          type="button"
          data-testid={`commitment-attachments-unlink-${row.link_id}`}
          aria-label={`Unlink ${row.filename} from this Commitment`}
          title="Unlink from Commitment (file stays in Account Documents)"
          disabled={isUnlinking}
          onClick={() => onUnlink(row)}
          className="group text-text-tertiary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUnlinking ? (
            <Loader2 size={12} aria-hidden="true" className="animate-spin" />
          ) : (
            <Unlink
              size={12}
              aria-hidden="true"
              className="group-hover:stroke-ember"
            />
          )}
        </button>
      )}
    </li>
  );
}


function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
